import React, { useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { rootUrl } from '../../../config/config';
import { selectPortalProps, numberInputClass, inputClass, labelClass } from './partsUi';

const emptyVehicle = { make_id: '', model_id: '', vehicle_year_start: '', vehicle_year_end: '', notes: '' };

/**
 * Compatible vehicles for a part. Saved vehicles (with an id) are removed on the server immediately;
 * new ones are kept in the list and saved by the parent when the part is saved.
 */
export default function CompatibleVehiclesEditor({ vehicles, onChange, vehicleMakes }) {
    const [draft, setDraft] = useState(emptyVehicle);
    const [models, setModels] = useState([]);
    const [error, setError] = useState('');
    const [removingIndex, setRemovingIndex] = useState(null);

    const handleMakeChange = async (makeId) => {
        setDraft({ ...draft, make_id: makeId, model_id: '' });
        setModels([]);
        if (!makeId) return;
        try {
            const token = localStorage.getItem('authToken') || 'default-token';
            const res = await axios.get(`${rootUrl}/vehicles/models?make_id=${makeId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setModels(Array.isArray(res.data.data) ? res.data.data : []);
        } catch (err) {
            console.error('Failed to fetch vehicle models:', err);
            setError('Could not load models for that make. Try selecting it again.');
        }
    };

    const addVehicle = () => {
        const make = vehicleMakes.find((m) => m.id === Number(draft.make_id));
        const model = models.find((m) => m.id === Number(draft.model_id));
        if (!make || !model) {
            setError('Select both a vehicle make and model to add it');
            return;
        }
        const start = draft.vehicle_year_start ? Number(draft.vehicle_year_start) : null;
        const end = draft.vehicle_year_end ? Number(draft.vehicle_year_end) : null;
        if (start && end && start > end) {
            setError('Year start cannot be after year end');
            return;
        }
        onChange([
            ...vehicles,
            {
                vehicle_make: make.name,
                vehicle_model: model.name,
                vehicle_year_start: start,
                vehicle_year_end: end,
                notes: draft.notes || ''
            }
        ]);
        setDraft(emptyVehicle);
        setModels([]);
        setError('');
    };

    const removeVehicle = async (index) => {
        const vehicle = vehicles[index];
        if (vehicle.id) {
            setRemovingIndex(index);
            try {
                const token = localStorage.getItem('authToken') || 'default-token';
                await axios.delete(`${rootUrl}/car_parts/compatibility/${vehicle.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } catch (err) {
                console.error('Failed to delete compatibility:', err);
                setError('Could not remove that vehicle. Please try again.');
                setRemovingIndex(null);
                return;
            }
            setRemovingIndex(null);
        }
        onChange(vehicles.filter((_, i) => i !== index));
    };

    const years = (v) => `${v.vehicle_year_start || 'Any'}–${v.vehicle_year_end || 'Any'}`;

    return (
        <div className="space-y-3">
            {vehicles.length === 0 ? (
                <p className="text-sm text-gray-500">No compatible vehicles added yet.</p>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {vehicles.map((v, idx) => (
                        <span
                            key={v.id || `new-${idx}`}
                            className="inline-flex items-center gap-2 max-w-full rounded-full bg-orange-50 border border-orange-200 pl-3 pr-1 py-1 text-sm text-gray-800"
                            title={v.notes || undefined}
                        >
                            <span className="truncate">
                                {v.vehicle_make} {v.vehicle_model} <span className="text-gray-500">({years(v)})</span>
                                {!v.id && <span className="ml-1 text-xs text-orange-600">new</span>}
                            </span>
                            <button
                                type="button"
                                onClick={() => removeVehicle(idx)}
                                disabled={removingIndex === idx}
                                className="p-1.5 rounded-full text-gray-500 hover:bg-orange-100 hover:text-red-600 disabled:opacity-50"
                                aria-label={`Remove ${v.vehicle_make} ${v.vehicle_model}`}
                            >
                                <FaTimes size={10} />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <div className="rounded-lg border border-dashed border-gray-300 p-3 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className={labelClass}>Vehicle Make</label>
                        <Select
                            {...selectPortalProps}
                            options={vehicleMakes.map((m) => ({ value: m.id, label: m.name }))}
                            value={draft.make_id ? { value: draft.make_id, label: vehicleMakes.find((m) => m.id === Number(draft.make_id))?.name } : null}
                            onChange={(option) => handleMakeChange(option?.value || '')}
                            placeholder="Select make"
                            isClearable
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Vehicle Model</label>
                        <Select
                            {...selectPortalProps}
                            options={models.map((m) => ({ value: m.id, label: m.name }))}
                            value={draft.model_id ? { value: draft.model_id, label: models.find((m) => m.id === Number(draft.model_id))?.name } : null}
                            onChange={(option) => setDraft({ ...draft, model_id: option?.value || '' })}
                            placeholder={draft.make_id ? 'Select model' : 'Select a make first'}
                            isClearable
                            isDisabled={!draft.make_id}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
                    <div>
                        <label className={labelClass}>Year From</label>
                        <input type="number" inputMode="numeric" className={numberInputClass} placeholder="2015" value={draft.vehicle_year_start} onChange={(e) => setDraft({ ...draft, vehicle_year_start: e.target.value })} />
                    </div>
                    <div>
                        <label className={labelClass}>Year To</label>
                        <input type="number" inputMode="numeric" className={numberInputClass} placeholder="2020" value={draft.vehicle_year_end} onChange={(e) => setDraft({ ...draft, vehicle_year_end: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                        <label className={labelClass}>Vehicle Notes</label>
                        <input className={inputClass} placeholder="Optional" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
                    </div>
                </div>
                {error && <p className="text-sm text-red-500">{error}</p>}
                <button
                    type="button"
                    onClick={addVehicle}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 h-10 rounded-lg border border-[#FF5733] text-[#FF5733] text-sm font-semibold hover:bg-orange-50"
                >
                    <FaPlus size={12} /> Add Vehicle
                </button>
            </div>
        </div>
    );
}
